import {
  AvatarGroup,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Backdrop,
  Badge,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import AvatarRem from "./AvatarRem";
import { TaskCardProps } from "model/activitiesModel";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Tooltip from "@mui/material/Tooltip";
import InsertCommentOutlinedIcon from "@mui/icons-material/InsertCommentRounded";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltRounded";
import FolderOutlinedIcon from "@mui/icons-material/FolderRounded";
import { getTranslation } from "common";
import { useTranslations, useUser } from "hooks";
import Labels from "./labels/Labels";
import {
  getLabelDataFromJsonTags,
  getParticipants,
  getInitials,
  getDisplayName,
  getUserData,
  getUserDataValue,
  LABEL_OFFSET,
} from "common/utilities";
import { baseApi, processesApi } from "api";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { cardFields } from "components/custom-fields/cardFields";
import { CALCULATED_MISSING_DATA_KEY, textForCalculatedValue } from "components/custom-fields/userdata";
import { attachmentNames } from "components/custom-fields/attachmentField";
import useCardPreferences from "hooks/useCardPreferences";
import HodDialogs from "./dialogs/HodDialogs";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useNavigate } from "react-router-dom";
import TaskQuickSwapButtons from "./TaskQuickSwapButtons";
import SequenceFlowsDialog from "./dialogs/SequenceFlowsDialog";
// SLA in ToDo (F3c): badge della riga, alimentato dal contesto della superficie
// SLA montata da `src/pages/MyActivities.tsx` (piano sez. 3 riga "Attivita'").
import SlaTaskSlaBadge from "./sla/SlaTaskSlaBadge";

// Un campo allegato vale un id o un array di id ("uno o molti" è reversibile).
const attachmentIdsOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(String).filter(Boolean)
    : typeof value === "string" && value !== ""
      ? [value]
      : [];


const TaskCard: React.FC<TaskCardProps> = ({
  task,
  labelCompleteMode,
  setDetail,
  onAssignClick,
  view,
  handleNextStep: onNextStep,
  handleBackStep,
  handleSequenceFlowChoice,
}) => {
  const translations = useTranslations();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const loggedInUser = useUser();
  const cardPreferences = useCardPreferences(task?.idprocessmodel);
  const visibleFields = cardFields(task, cardPreferences);

  // Nomi dei file per i campi allegato in card: basta la GET light (id e nome).
  // `useAttachments` scaricherebbe anche blob e storie, troppo per una lista.
  const attachmentLookupKey = visibleFields
    .filter(([, value]) => value.type === "attachment")
    .flatMap(([, value]) => attachmentIdsOf(value.value))
    .join("|");
  const [availableAttachments, setAvailableAttachments] = useState<
    { id: number; filename: string }[] | null
  >(null);

  useEffect(() => {
    const ids = attachmentLookupKey ? attachmentLookupKey.split("|") : [];
    const idprocess = task?.idprocess;
    if (!idprocess || ids.length === 0) return;

    // Legato a `task`: un refresh della lista porta un oggetto nuovo e i nomi
    // riletti, così una rinomina arriva al render successivo.
    let cancelled = false;
    processesApi
      .get(`${idprocess}/attachments/light`)
      .then((response) => {
        if (!cancelled) setAvailableAttachments(response.data.attachments ?? []);
      })
      .catch(() => {
        // Silenzioso come gli altri fetch della card: restano gli id.
      });
    return () => {
      cancelled = true;
    };
  }, [task, attachmentLookupKey]);

  const [groupUsers, setGroupUsers] = useState<
    {
      id: number;
      name: string;
      firstname: string;
      lastname: string;
      picture: string;
    }[]
  >([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [openSequenceDialog, setOpenSequenceDialog] = useState(false);

  const handleNextStep = (task: TaskCardProps["task"]) => {
    if (
      task.state === 1 &&
      task.sequenceflows &&
      task.sequenceflows.length > 1 &&
      handleSequenceFlowChoice
    ) {
      setOpenSequenceDialog(true);
      return;
    }

    if (task.state === 1 && task.sequenceflows?.length === 1 && handleSequenceFlowChoice) {
      handleSequenceFlowChoice(task, task.sequenceflows[0]);
      return;
    }

    onNextStep?.(task);
  };
  const canHandleNextStep = Boolean(onNextStep || handleSequenceFlowChoice);

  const handleAssignClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDialogOpen(true);
    setLoadingUsers(true);

    try {
      const response = await baseApi.get(
        `/tasks/processtoken/${task.idprocesstoken}/users`
      );
      setGroupUsers(response.data.users);
    } catch {
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (user: {
    id: number;
    firstname: string;
    lastname: string;
  }) => {
    setSelectedUser({
      id: user.id,
      name: `${user.firstname} ${user.lastname}`,
    });
    setDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAssign = async () => {
    if (selectedUser) {
      try {
        await baseApi.put(
          `/tasks/processtoken/${task.idprocesstoken}/assignment/user/${selectedUser.id}`,
          {
            iduser: selectedUser.id,
          }
        );
      } catch {
      } finally {
        setConfirmDialogOpen(false);
        setSelectedUser(null);
      }
    }
  };

  // Il contatore dei campi è quello che la card ha davvero: le voci
  // userschema presenti nell'userdata della riga. Il `countuserdatafields` del
  // server conta il vecchio `processes.userdata`, che con i custom fields resta
  // vuoto (i valori vivono in `cf_process_state`), quindi valeva sempre 0.
  const userschemaCount = Object.keys(getUserData(task?.userdata)).length;
  const icons = [
    {
      title: getTranslation(translations, "settings.comments.label"),
      IconComponent: InsertCommentOutlinedIcon,
      count: task?.countcomments,
    },
    {
      title: getTranslation(translations, "generic.fields.caption"),
      IconComponent: ListAltOutlinedIcon,
      count: userschemaCount,
    },
    {
      title: getTranslation(translations, "generic.attached_files.caption"),
      IconComponent: FolderOutlinedIcon,
      count: task?.countattachments,
    },
  ];

  const onButtonClickIcon = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setDetail(task?.idprocesstoken);
  };

  // const typographyStyleDescription = {
  //   fontSize: 16,
  //   fontWeight: 700,
  //   whiteSpace: "nowrap",
  //   overflow: "hidden",
  // };

  const chipStylePriority = (theme: any) => ({
    "& .MuiChip-label": {
      px: 0,
    },
    backgroundColor:
      theme.palette.mode === "dark" ? `${theme.palette.primary.main}24` : "transparent",
    fontSize: 14,
    fontWeight: "bold",
    minWidth: 45,
    px: 1,
    borderRadius: "6px",
    border: `1.5px solid ${theme.palette.primary.main}`,
    height: 20,
    color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.text.primary,
  });

  const getDateChipSx = (theme: any, compact = false) => ({
    "& .MuiChip-label": {
      px: compact ? 0.5 : 0,
    },
    fontSize: compact ? 11 : 13,
    width: compact ? undefined : 40,
    borderRadius: 1,
    marginRight: compact ? undefined : 1,
    height: compact ? 18 : 20,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.background.paper,
  });

  const getCardPadding = () => {
    if (view === "minimal") return "8px";
    if (view === "compact") return "8px";
    return "10px";
  };

  const navigate = useNavigate();

  return (
    <React.Fragment>
      <Card
        sx={(theme) => ({
          position: "relative",
          minHeight: view === "minimal" ? 60 : view === "compact" ? 110 : undefined,
          minWidth: 0,
          flex: 1,
          padding: getCardPadding(),
          width: "calc(100% - 32px)",
          borderRadius: view === "minimal" ? "12px" : "16px",
          display: "flex",
          cursor: "grab",
          // In tema scuro una card elevata si confonde con lo sfondo: un filo
          // di grigio la stacca, senza il peso di un'ombra più marcata. In
          // chiaro il bordo non serve e tornerebbe a fare da cornice.
          border:
            theme.palette.mode === "dark"
              ? `1px solid ${theme.palette.grey[700]}`
              : "none",
        })}
        variant="elevation"
        elevation={3}
      >
        <Backdrop
          open={!!task.loading}
          component="div"
          sx={{
            position: "absolute",
            zIndex: 9999,
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Backdrop>
        <Stack sx={{ maxWidth: "100%", minWidth: 0, flex: 1 }}>
          <Stack
            flex={1}
            direction="row"
            sx={{ position: "relative", maxWidth: "100%", minWidth: 0 }}
          >
            <CardContent
              component={Stack}
              direction="column"
              flex={1}
              sx={{ maxWidth: "100%", minWidth: 0 }}
              p={"0px!important"}
            >
              {/* MINIMAL VIEW */}
              {view === "minimal" && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  height="100%"
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      fontSize: 14,
                    }}
                  >
                    {task?.description}
                  </Typography>
                  <Box sx={{ ml: 1, display: "flex", minWidth: 0 }}>
                    <SlaTaskSlaBadge task={task} variant="compact" />
                  </Box>
                  {task.assignmentmode === 2 && task.iduser === 0 && (
                    <Tooltip
                      title={getTranslation(
                        translations,
                        "generic.assign_task_to_user"
                      )}
                    >
                      <IconButton
                        id="hod-dialog-button"
                        size="small"
                        onClick={handleAssignClick}
                      >
                        <PersonAddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.grey[200]}`,
                      borderRadius: "5px",
                      padding: "2px",
                      ml: 1,
                    })}
                    className="detail-btn"
                    id={`task-detail-btn-${task?.idprocesstoken}`}
                    disabled={task?.loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetail(task?.idprocesstoken);
                    }}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                  {/* <TaskQuickSwapButtons
                    task={task}
                    onBack={handleBackStep}
                    onNext={handleNextStep}
                  /> */}
                </Box>
              )}

              {/* COMPACT VIEW */}
              {view === "compact" && (
                <Box display="flex" flexDirection="column" flex={1}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={0.5}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        flex: 1,
                        fontSize: 14,
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {task?.description}
                    </Typography>
                    <IconButton
                      sx={(theme) => ({
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: "5px",
                        padding: "2px",
                        ml: 1,
                      })}
                      className="detail-btn"
                      id={`task-detail-btn-${task?.idprocesstoken}`}
                      disabled={task?.loading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetail(task?.idprocesstoken);
                      }}
                    >
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                    <Tooltip title={getTranslation(translations, "pm.label.caption")} arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/process/edit/${task.idprocessmodel}`, { state: { designerid: task.designerid } });
                        }}
                        sx={{ mr: 1 }}
                      >
                        <AccountTreeIcon sx={{ color: "primary.main", ":hover": { color: "secondary.main" }, ml: -1 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "text.secondary",
                        fontSize: 12,
                        mb: 0.5,
                      }}
                    >
                      {task?.processmodelname}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt="auto"
                  >
                    <Box sx={{ display: "flex", minWidth: 0 }}>
                      <SlaTaskSlaBadge task={task} variant="compact" />
                    </Box>
                    <Box display="flex" gap={2} marginLeft="auto" marginRight={2}>
                      {task.assignmentmode === 2 && task.iduser === 0 && (
                        <Tooltip
                          title={getTranslation(
                            translations,
                            "generic.assign_task_to_user"
                          )}
                        >
                          <IconButton
                            id="hod-dialog-button"
                            size="small"
                            onClick={handleAssignClick}
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {icons.map((tooltip, index) => {
                        const count = tooltip.count ?? 0;
                        return count > 0 ? (
                          <Tooltip
                            key={index}
                            title={tooltip.title}
                            arrow
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              id={`task-detail-btn-${tooltip.title}`}
                              onClick={onButtonClickIcon}
                              sx={{ p: 0.25 }}
                            >
                              <Badge
                                badgeContent={count}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: 9,
                                    height: 14,
                                    minWidth: 14,
                                  },
                                }}
                              >
                                <tooltip.IconComponent sx={{ fontSize: 18 }} />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        ) : null;
                      })}
                    </Box>
                    <Chip
                      sx={(theme) => getDateChipSx(theme, true)}
                      label={dayjs(task?.created).format("DD/MM")}
                    />
                    {/* <TaskQuickSwapButtons
                      task={task}
                      onBack={handleBackStep}
                      onNext={handleNextStep}
                    /> */}
                  </Box>
                </Box>
              )}

              {/* EXPANDED VIEW (stile originale) */}
              {view === "extended" && (
                <>
                  <Box sx={{
                    minHeight: 10 + (labelCompleteMode ? LABEL_OFFSET : 0),
                    "& > .MuiBox-root": { flexWrap: "wrap" },
                    "& .MuiChip-root": { maxWidth: "100%" },
                  }}>
                    <Labels
                      labelCompleteMode={labelCompleteMode}
                      inCard
                      labels={getLabelDataFromJsonTags(task?.jsontag)}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flex: 1,
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    <Stack
                      sx={{
                        maxWidth: "100%",
                        minWidth: 0,
                        flex: 1,
                        p: 0,
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ maxWidth: "100%", minWidth: 0 }}
                        alignItems="flex-start"
                      >
                        {visibleFields.map(([key, value]) => {
                          const valueText = value.type === "calculated"
                            ? textForCalculatedValue(value.value, getTranslation(translations, CALCULATED_MISSING_DATA_KEY))
                            : value.type === "attachment"
                              ? attachmentNames(attachmentIdsOf(value.value), availableAttachments ?? [])
                                  .map((name) =>
                                    name.filename ??
                                    (availableAttachments
                                      ? getTranslation(translations, "custom_fields.attachment.missing")
                                      : name.id)
                                  )
                                  .join(", ")
                              : getUserDataValue(value);
                          const displayValue = `${key}: ${valueText}`;

                          // `cardFields` ha gia' filtrato su autorizzazione e
                          // inCard effettivo (preferenza personale compresa): qui si
                          // rende sempre. La chiave `cf-card-field-<chiave>`
                          // resta la prova che un campo non autorizzato non
                          // arriva nel DOM.
                          return (
                            <React.Fragment key={key}>
                              <Box
                                data-testid={`cf-card-field-${key}`}
                                display="flex"
                                sx={{
                                  maxWidth: "100%",
                                  minWidth: 0,
                                }}
                                alignItems="center"
                              >
                                <Tooltip
                                  title={displayValue}
                                  placement="bottom-start"
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="text.disabled"
                                    sx={{
                                      fontSize: 12,
                                      overflowWrap: "anywhere",
                                      fontWeight: "bold",
                                      // Il nome dell'allegato arriva dopo la GET
                                      // light: un nome lungo andava a capo e
                                      // cambiava l'altezza della card a risposta
                                      // ricevuta, ri-misurando la riga e facendo
                                      // scattare lo scroll. Una riga con ellissi
                                      // (testo intero nel tooltip) la tiene ferma.
                                      ...(value.type === "attachment"
                                        ? {
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            minWidth: 0,
                                          }
                                        : {}),
                                    }}
                                  >
                                    {/* La key pesa piu' del valore: a colpo d'occhio
                                        si legge il nome del campo, non il dato. */}
                                    {/* Il colore viene dal tema: il nero fisso
                                        spariva sul fondo scuro della card. */}
                                    <Box component="span" sx={{ fontWeight: 800, color: "text.primary" }}>{key}</Box>
                                    {`: ${valueText}`}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </React.Fragment>
                          );
                        })}
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                      >
                        {task?.flownodename && (
                          <Typography variant="caption" sx={{ paddingTop: "1px", minWidth: 0, overflowWrap: "anywhere" }}>
                            {task.flownodename}
                          </Typography>
                        )}
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            justifyContent: "end",
                            alignItems: "center",
                          }}
                        >
                          <Chip
                            sx={(theme) => getDateChipSx(theme)}
                            label={dayjs(task?.created).format("DD/MM")}
                          />
                          <IconButton
                            sx={(theme) => ({
                              border: `1px solid ${theme.palette.grey[200]}`,
                              borderRadius: "5px",
                              padding: "2px",
                            })}
                            className="detail-btn"
                            id={`task-detail-btn-${task?.idprocesstoken}`}
                            aria-label={getTranslation(
                              translations,
                              "generic.add_box.caption"
                            )}
                            disabled={task?.loading}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetail(task?.idprocesstoken);
                            }}
                          >
                            <MoreHorizIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box pt={1}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <SlaTaskSlaBadge task={task} variant="compact" />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              ml: "auto",
                              minWidth: 0,
                            }}
                          >
                          {task?.priority_name &&
                            (task?.priority_name.length > 6 ? (
                              <Tooltip
                                title={task?.priority_name}
                                placement="bottom-start"
                              >
                                <Chip
                                  sx={chipStylePriority}
                                  label={`${task?.priority_name.substring(
                                    0,
                                    6
                                  )}...`}
                                />
                              </Tooltip>
                            ) : (
                              <Chip
                                sx={chipStylePriority}
                                label={task?.priority_name}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                          <Tooltip title={getTranslation(translations, "pm.label.caption")} arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/process/edit/${task.idprocessmodel}`, { state: { designerid: task.designerid } });
                              }}
                              sx={{ mr: 1 }}
                            >
                              <AccountTreeIcon sx={{ fontSize: 18, color: "primary.main", ":hover": { color: "secondary.main" }, ml: -1 }} />
                            </IconButton>
                          </Tooltip>
                          <Typography
                            sx={{
                              minWidth: 0,
                              overflowWrap: "anywhere",
                            }}
                            color="text.disabled"
                          >
                            {task?.processmodelname}
                          </Typography>
                        </Box>
                      </Box>
                      <Box py={1}>
                        {task?.description && (
                          <Typography
                            sx={{
                              width: "100%",
                              overflowWrap: "anywhere",
                              fontWeight: "bold"
                            }}
                          >
                            {task.description}
                          </Typography>
                        )}
                        <Divider
                          sx={{
                            borderColor: "lightgrey",
                            // marginBottom: "5px",
                            marginTop: "5px",
                          }}
                        />
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                        mt={1}
                      >
                        {(() => {
                          const isDoing = task.state === 1;

                          if (isDoing && loggedInUser) {
                            return null;
                          }
                          const participants = getParticipants(task.jsonparticipant);
                          if (participants.length === 0) {
                            return <AvatarRem sx={{ height: 30, width: 30 }} />;
                          }
                          const allParticipants = [...participants];
                          const tooltipTitle = allParticipants.map(p => getDisplayName(p)).join(', ');

                          return (
                            <Tooltip title={tooltipTitle}>
                              <span>
                                <AvatarGroup
                                  max={5}
                                  sx={{
                                    "& .MuiAvatar-root": {
                                      width: 30,
                                      height: 30,
                                      fontSize: "0.8rem",
                                    },
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  {allParticipants.map((p) => {
                                    let iconUrl = `/users/${p.idparticipant}/picture`;

                                    if (p.participanttype === 1) {
                                      iconUrl = `/downloads/groups/${p.idparticipant}`;
                                    } else if (p.participanttype === 2) {
                                      iconUrl = `/downloads/roles/${p.idparticipant}`;
                                    }
                                    return (
                                      <AvatarRem
                                        key={p.idparticipant}
                                        icon={iconUrl}
                                      >
                                        {getInitials(p)}
                                      </AvatarRem>
                                    );
                                  })}
                                </AvatarGroup>
                              </span>
                            </Tooltip>
                          );
                        })()}
                        <Box sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                          ml: "auto",
                          gap: 0.5,
                          alignItems: "center",
                        }} >
                          {task.assignmentmode === 2 && task.iduser === 0 && (
                            <Tooltip
                              title={getTranslation(
                                translations,
                                "generic.assign_task_to_user"
                              )}
                            >
                              <IconButton
                                id="hod-dialog-button"
                                size="small"
                                onClick={handleAssignClick}
                              >
                                <PersonAddIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {icons.map((tooltip, index) => (
                            <Tooltip
                              key={index}
                              title={tooltip.title}
                              arrow
                              placement="top"
                            >
                              <IconButton
                                id={`task-detail-btn-${tooltip.title}`}
                                aria-label={`Add Box (${tooltip.title})`}
                                onClick={onButtonClickIcon}
                              >
                                <Badge
                                  badgeContent={tooltip.count}
                                  color="primary"
                                >
                                  <tooltip.IconComponent />
                                </Badge>
                              </IconButton>
                            </Tooltip>
                          ))}
                          <TaskQuickSwapButtons
                            task={task}
                            onBack={handleBackStep}
                            onNext={canHandleNextStep ? handleNextStep : undefined}
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </>
              )}
            </CardContent>
          </Stack>
        </Stack>
      </Card>
      <HodDialogs
        isDialogOpen={isDialogOpen}
        setDialogOpen={setDialogOpen}
        loadingUsers={loadingUsers}
        groupUsers={groupUsers}
        handleUserSelect={handleUserSelect}
        isConfirmDialogOpen={isConfirmDialogOpen}
        setConfirmDialogOpen={setConfirmDialogOpen}
        selectedUser={selectedUser}
        handleConfirmAssign={handleConfirmAssign}
      />
      <SequenceFlowsDialog
        gatewayQuestion={task.flownodename}
        open={openSequenceDialog}
        onClose={() => setOpenSequenceDialog(false)}
        sequenceFlows={task.sequenceflows ?? []}
        onSelect={(sequenceFlow) => {
          handleSequenceFlowChoice?.(task, sequenceFlow);
          setOpenSequenceDialog(false);
        }}
      />
    </React.Fragment>
  );
};

export default TaskCard;
